type Payload = Record<string, unknown>;

/** Curates event payloads so large scene/entity documents are never persisted. */
export function buildTelemetryPropsJson(type: string, value: unknown): string | null {
  const p = record(value);
  if (!p) return null;
  const props = propsFor(type, p);
  return props ? JSON.stringify(props) : null;
}

function propsFor(type: string, p: Payload): Payload | null {
  switch (type) {
    case "launch_application":
    case "quit_application":
      return pick(p, {
        AppName: ["AppName", "appName", "app_name"],
        OriginatingAppInstanceId: ["OriginatingAppInstanceId", "aid"],
        OriginatingDeviceId: ["OriginatingDeviceId", "DeviceId", "device_id"],
        DeviceType: ["DeviceType", "deviceType"],
      });
    case "create_project":
    case "delete_project":
    case "unload_project":
    case "load_project":
      return project(p);
    case "close_project":
      return { ...project(p), ...pick(p, { Duration: ["Duration", "duration"] }) };
    case "update_project":
      return {
        ...project(p),
        ...pick(p, {
          ProjectName: ["ProjectName", "projectName"],
          HasUserDisplaySettings: ["HasUserDisplaySettings", "hasUserDisplaySettings"],
          HideAvatars: ["HideAvatars", "hideAvatars"],
          HideUserTags: ["HideUserTags", "hideUserTags"],
          HasConsoleAssetSettings: [
            "HasConsoleAssetSettings",
            "hasConsoleAssetSettings",
          ],
          ConsoleAssetID: ["ConsoleAssetID", "consoleAssetId"],
        }),
      };
    case "open_project":
      return {
        ...project(p),
        ...pick(p, {
          ProjectName: ["ProjectName", "projectName"],
          UserRole: ["UserRole", "userRole"],
          OriginatingAppInstanceId: ["OriginatingAppInstanceId", "aid"],
          OriginatingDeviceId: ["OriginatingDeviceId", "DeviceId", "device_id"],
        }),
      };
    case "evict_user":
      return { ...project(p), ...pick(p, { UserId: ["UserId", "userId"] }) };
    case "change_sharing":
      return {
        ...project(p),
        ...pick(p, {
          AccountId: ["AccountId", "accountId"],
          UserId: ["UserId", "userId"],
          ShareEvent: ["ShareEvent", "shareEvent"],
          UserRole: ["UserRole", "userRole"],
        }),
      };
    case "presenter_change":
      return pick(p, { PresenterId: ["PresenterId", "presenterId"] });
    case "writer_change":
      return pick(p, { WriterId: ["WriterId", "writerId"] });
    case "follower_change":
      return pick(p, { FollowedUserKey: ["FollowedUserKey", "followedUserKey"] });
    case "ask_to_follow":
      return {
        ...project(p),
        ...pick(p, { AsktoFollowUserKey: ["AsktoFollowUserKey", "asktoFollowUserKey"] }),
      };
    case "create_scene": {
      const sceneData = record(first(p, "SceneData", "sceneData")) ?? {};
      return {
        ...scene(p),
        ...pick(sceneData, {
          SceneName: ["Name", "name"],
          SceneType: ["SceneType", "sceneType"],
          SceneMode: ["SceneMode", "sceneMode"],
        }),
      };
    }
    case "delete_scene":
    case "load_scene":
      return scene(p);
    case "unload_scene":
      return { ...scene(p), ...pick(p, { Duration: ["Duration", "duration"] }) };
    case "create_entity":
    case "delete_entity":
      return {
        ...scene(p),
        ...pick(p, { AssetId: ["AssetId", "assetId"], EntityId: ["EntityId", "entityId"] }),
      };
    case "update_entitysnapshot": {
      const snapshot = record(first(p, "EntitySnapshot", "entitySnapshot")) ?? {};
      return {
        ...scene(p),
        ...pick(snapshot, {
          EntityId: ["EntityID", "entityId"],
          SnapshotId: ["SnapshotID", "snapshotId"],
          Name: ["Name", "name"],
        }),
        ...pick(p, { UpdateEntityFlags: ["UpdateEntityFlags", "updateEntityFlags"] }),
      };
    }
    case "active_tool":
      return {
        ...project(p),
        ...pick(p, {
          ActiveTool: ["ActiveTool", "activeTool", "at"],
          ToolVisibility: ["ToolVisibility", "tool_visibility"],
        }),
      };
    case "tool_action":
      return {
        ...project(p),
        ...pick(p, { Tool: ["Tool", "tool"], ActionIndex: ["ActionIndex", "actionIndex"] }),
      };
    case "grabtool_properties": {
      const properties = record(first(p, "Properties", "properties")) ?? {};
      return {
        ...project(p),
        ...pick(properties, { SelectionMode: ["SelectionMode", "selectionMode"] }),
      };
    }
    case "flashlight_properties": {
      const properties = record(first(p, "Properties", "properties")) ?? {};
      return {
        ...project(p),
        ...pick(properties, {
          FlashlightIntensity: ["FlashlightIntensity", "flashlightIntensity"],
          FlashlightAngle: ["FlashlightAngle", "flashlightAngle"],
          FlashlightType: ["FlashlightType", "flashlightType"],
        }),
      };
    }
    case "sketch_uploaded":
      return {
        ...scene(p),
        ...pick(p, { SketchId: ["SketchId", "sketchId"] }),
      };
    case "anchor_access":
      return {
        ...project(p),
        ...pick(p, {
          SharerAccountId: ["SharerAccountId", "sharerAccountId"],
          ProjectSSAID: ["ProjectSSAID", "projectSSAID"],
          AnchorAccessEventType: ["AnchorAccessEventType", "anchorAccessEventType"],
        }),
      };
    case "reserve_color":
      return pick(p, { ColorIndex: ["ColorIndex", "color_index", "ci"] });
    case "create_annotation":
    case "edit_annotation": {
      const update = record(first(p, "Update", "update")) ?? {};
      return {
        ...scene(p, update),
        ...pick(update, { AnnotationId: ["ID", "id"], UpdateFlags: ["UpdateFlags", "updateFlags"] }),
      };
    }
    case "delete_annotation":
      return { ...scene(p), ...pick(p, { AnnotationId: ["AnnotationId", "annotationId"] }) };
    case "create_measurement": {
      const measurement = record(first(p, "Measurement", "measurement")) ?? {};
      return {
        ...scene(p, measurement),
        ...pick(measurement, {
          MeasurementId: ["ID", "id"],
          StartEntityId: ["StartEntityId", "startEntityId"],
          EndEntityId: ["EndEntityId", "endEntityId"],
        }),
      };
    }
    case "delete_measurement":
      return { ...scene(p), ...pick(p, { MeasurementId: ["MeasurementId", "measurementId"] }) };
    case "calibration_action":
      return pick(p, {
        CalibrationState: ["CalibrationState", "calibrationState"],
        DominantHand: ["DominantHand", "dominantHand"],
        IPD: ["IPD", "ipd"],
      });
    case "update_selectionset": {
      const data = record(first(p, "Data", "data")) ?? {};
      return {
        ...scene(p),
        ...pick(p, { Action: ["Action", "action"] }),
        ...pick(data, { SelectionSetId: ["ID", "id"] }),
        EntityCount: arrayLength(data, "EntitySnapshotIDs", "entitySnapshotIds"),
        MediaTileCount: arrayLength(data, "MediaTileIDs", "mediaTileIds"),
      };
    }
    case "update_folder": {
      const folder = record(first(p, "Folder", "folder")) ?? {};
      return {
        ...pick(p, { FolderEventType: ["FolderEventType", "folderEventType"] }),
        ...pick(folder, { FolderId: ["ID", "id"], FolderName: ["Name", "name"] }),
        ProjectCount: arrayLength(folder, "ProjectIDs", "projectIds"),
      };
    }
    case "update_mediaasset":
    case "delete_mediatile":
      return {
        ...scene(p),
        ...pick(p, { MediaTileId: ["MediaTileId", "mediaTileId", "mtid"] }),
      };
    case "create_mediatile": {
      const tile = record(first(p, "MediaTileData", "mediaTileData", "data", "mt")) ?? {};
      return {
        ...scene(p),
        ...pick(tile, {
          MediaTileId: ["Id", "ID", "id"],
          AssetId: ["AssetId", "assetId"],
          Name: ["Name", "name"],
        }),
      };
    }
    default:
      return null;
  }
}

function project(p: Payload): Payload {
  return pick(p, { ProjectId: ["ProjectId", "projectId", "project_id"] });
}

function scene(p: Payload, fallback: Payload = {}): Payload {
  return {
    ...project(p),
    ...pick({ ...fallback, ...p }, { SceneId: ["SceneId", "SceneID", "sceneId"] }),
  };
}

function pick(p: Payload, fields: Record<string, ReadonlyArray<string>>): Payload {
  const output: Payload = {};
  for (const [name, keys] of Object.entries(fields)) {
    const value = first(p, ...keys);
    if (value !== undefined && value !== null) output[name] = value;
  }
  return output;
}

function first(p: Payload, ...keys: ReadonlyArray<string>): unknown {
  for (const key of keys) if (p[key] !== undefined && p[key] !== null) return p[key];
  return undefined;
}

function arrayLength(p: Payload, ...keys: ReadonlyArray<string>): number {
  const value = first(p, ...keys);
  return Array.isArray(value) ? value.length : 0;
}

function record(value: unknown): Payload | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? value as Payload
    : null;
}
